// 📂 src/pages/CameraDetail/components/ExportDialog.jsx
// Enterprise clip export — IN/OUT markers, format selection, professional modal

import { useState, useMemo, useEffect } from "react";
import {
  FiX,
  FiDownload,
  FiScissors,
  FiAlertCircle,
  FiCheck,
  FiFilm,
  FiClock,
  FiTag,
} from "react-icons/fi";

const FORMATS = [
  { id: "mp4", label: "MP4", desc: "H.264 — Best compatibility", icon: "🎬" },
  { id: "mkv", label: "MKV", desc: "Matroska — Preserves all streams", icon: "📦" },
  { id: "avi", label: "AVI", desc: "Legacy — Broad support", icon: "📼" },
];

const QUALITY_OPTIONS = [
  { id: "original", label: "Original", desc: "No re-encoding" },
  { id: "high", label: "High", desc: "~8 Mbps" },
  { id: "medium", label: "Medium", desc: "~4 Mbps" },
  { id: "low", label: "Low", desc: "~1.5 Mbps" },
];

function formatTimecode(date, tz = "America/Vancouver") {
  if (!date) return "--:--:--";
  return new Date(date).toLocaleTimeString("en-CA", {
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tz,
  });
}

function formatDurationMs(ms) {
  if (!ms || ms <= 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(" ");
}

export default function ExportDialog({
  isOpen,
  onClose,
  inPoint,
  outPoint,
  currentTime,
  onSetInPoint,
  onSetOutPoint,
  onClearPoints,
  onExport,
  camera,
  timezone = "America/Vancouver",
}) {
  const [format, setFormat] = useState("mp4");
  const [quality, setQuality] = useState("original");
  const [burnTimestamp, setBurnTimestamp] = useState(false);
  const [burnCameraName, setBurnCameraName] = useState(false);
  const [filename, setFilename] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);

  // Generate default filename
  useEffect(() => {
    if (isOpen && !filename) {
      const cam = camera?.name || "camera";
      const ts = (inPoint || currentTime || new Date()).toISOString().slice(0, 19).replace(/[T:]/g, "-");
      setFilename(`${cam}_${ts}`);
    }
  }, [isOpen, camera, inPoint, currentTime, filename]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setIsExporting(false);
      setExportDone(false);
    }
  }, [isOpen]);

  const clipDuration = useMemo(() => {
    if (!inPoint || !outPoint) return null;
    return new Date(outPoint).getTime() - new Date(inPoint).getTime();
  }, [inPoint, outPoint]);

  const canExport = inPoint && outPoint && clipDuration > 0;

  const handleExport = async () => {
    if (!canExport) return;
    setIsExporting(true);
    try {
      await onExport?.({
        inPoint, outPoint, format, quality,
        burnTimestamp, burnCameraName,
        filename: filename || "export",
      });
      setExportDone(true);
      setTimeout(() => onClose?.(), 1500);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Dialog */}
      <div className="relative w-full max-w-lg bg-[#141414] border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <FiScissors className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold">Export Clip</h3>
              <p className="text-[11px] text-gray-500">{camera?.name || "Camera"}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/5">
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* IN / OUT points */}
          <div>
            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 block">
              Clip Range
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* IN Point */}
              <div className="bg-white/5 border border-white/8 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wider">IN</span>
                  <button
                    onClick={() => onSetInPoint?.(currentTime || new Date())}
                    className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                    title="Set to current time (I)"
                  >
                    Set Now
                  </button>
                </div>
                <div className="text-white text-sm font-mono tabular-nums">
                  {inPoint ? formatTimecode(inPoint, timezone) : (
                    <span className="text-gray-600">Not set</span>
                  )}
                </div>
              </div>

              {/* OUT Point */}
              <div className="bg-white/5 border border-white/8 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-semibold text-red-400 uppercase tracking-wider">OUT</span>
                  <button
                    onClick={() => onSetOutPoint?.(currentTime || new Date())}
                    className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                    title="Set to current time (O)"
                  >
                    Set Now
                  </button>
                </div>
                <div className="text-white text-sm font-mono tabular-nums">
                  {outPoint ? formatTimecode(outPoint, timezone) : (
                    <span className="text-gray-600">Not set</span>
                  )}
                </div>
              </div>
            </div>

            {/* Duration + Clear */}
            <div className="flex items-center justify-between mt-2">
              {clipDuration != null && clipDuration > 0 ? (
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                  <FiClock className="w-3 h-3" />
                  <span>Duration: <span className="text-white font-mono">{formatDurationMs(clipDuration)}</span></span>
                </div>
              ) : (
                <span />
              )}
              {(inPoint || outPoint) && (
                <button
                  onClick={onClearPoints}
                  className="text-[11px] text-gray-500 hover:text-red-400 transition-colors"
                >
                  Clear points
                </button>
              )}
            </div>
          </div>

          {/* Format */}
          <div>
            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 block">
              Format
            </label>
            <div className="grid grid-cols-3 gap-2">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  className={`p-2.5 rounded-lg text-left transition-all border ${
                    format === f.id
                      ? "bg-blue-500/10 border-blue-500/30 ring-1 ring-blue-500/20"
                      : "bg-white/3 border-white/5 hover:bg-white/5 hover:border-white/10"
                  }`}
                >
                  <div className="text-sm mb-0.5">
                    <span className="mr-1">{f.icon}</span>
                    <span className={format === f.id ? "text-white font-medium" : "text-gray-300"}>{f.label}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 leading-tight">{f.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Quality */}
          <div>
            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 block">
              Quality
            </label>
            <div className="flex gap-1.5">
              {QUALITY_OPTIONS.map((q) => (
                <button
                  key={q.id}
                  onClick={() => setQuality(q.id)}
                  className={`flex-1 py-2 px-2 rounded-lg text-center transition-all border text-xs ${
                    quality === q.id
                      ? "bg-blue-500/10 border-blue-500/30 text-white font-medium"
                      : "bg-white/3 border-white/5 text-gray-400 hover:bg-white/5"
                  }`}
                  title={q.desc}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* Filename */}
          <div>
            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 block">
              Filename
            </label>
            <div className="relative">
              <FiTag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                className="w-full bg-white/5 border border-white/8 rounded-lg pl-9 pr-16 py-2.5 text-sm text-white font-mono placeholder-gray-600 focus:outline-none focus:border-blue-500/40 transition-colors"
                placeholder="export_filename"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 font-mono">.{format}</span>
            </div>
          </div>

          {/* Burn-in options */}
          <div>
            <label className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2 block">
              Overlay
            </label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    burnTimestamp ? "bg-blue-600 border-blue-600" : "border-gray-600 group-hover:border-gray-400"
                  }`}
                  onClick={() => setBurnTimestamp(!burnTimestamp)}
                >
                  {burnTimestamp && <FiCheck className="w-3 h-3 text-white" />}
                </div>
                <span className="text-xs text-gray-300" onClick={() => setBurnTimestamp(!burnTimestamp)}>Timestamp</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer group">
                <div
                  className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                    burnCameraName ? "bg-blue-600 border-blue-600" : "border-gray-600 group-hover:border-gray-400"
                  }`}
                  onClick={() => setBurnCameraName(!burnCameraName)}
                >
                  {burnCameraName && <FiCheck className="w-3 h-3 text-white" />}
                </div>
                <span className="text-xs text-gray-300" onClick={() => setBurnCameraName(!burnCameraName)}>Camera name</span>
              </label>
            </div>
          </div>

          {/* Warning if points not set */}
          {(!inPoint || !outPoint) && (
            <div className="flex items-start gap-2.5 p-3 bg-yellow-500/5 border border-yellow-500/15 rounded-lg">
              <FiAlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
              <div className="text-xs text-yellow-500/80 leading-relaxed">
                Set both IN and OUT points to export a clip. Use <kbd className="px-1 py-0.5 bg-white/10 rounded text-[10px] font-mono">I</kbd> and <kbd className="px-1 py-0.5 bg-white/10 rounded text-[10px] font-mono">O</kbd> keys during playback, or click "Set Now" above.
              </div>
            </div>
          )}

          {/* Export complete */}
          {exportDone && (
            <div className="flex items-center gap-2.5 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <FiCheck className="w-4 h-4 text-green-400" />
              <span className="text-xs text-green-400">Export complete — download started.</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-white/8 bg-white/[0.02]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={!canExport || isExporting}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              canExport && !isExporting
                ? "bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20"
                : "bg-white/5 text-gray-500 cursor-not-allowed"
            }`}
          >
            <FiDownload className="w-4 h-4" />
            {isExporting ? "Exporting…" : exportDone ? "Done!" : "Export Clip"}
          </button>
        </div>
      </div>
    </div>
  );
}
